import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Button, TextField, Chip, IconButton, MenuItem, Select,
  InputAdornment, TablePagination, Avatar, Tooltip, CircularProgress
} from '@mui/material';
import {
  Add, Search, Edit, Person, Phone, Email, FilterList, Visibility, Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API } from '../context/AuthContext';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import ClientFormDialog from '../components/clients/ClientFormDialog';
import { useSnackbar } from 'notistack';

const STATUS_OPTIONS = ['', 'prospecto', 'activo', 'inactivo', 'vip'];

export default function ClientsPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/clients', { params: { search, status, page: page + 1, limit: 15 } });
      setClients(data.clients);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSave = () => { setFormOpen(false); setEditClient(null); fetchClients(); };

  const deleteClient = async (id, name) => {
    if (!window.confirm(`¿Eliminar permanentemente al cliente "${name}"? Solo es posible si no tiene cotizaciones ni facturas vinculadas.`)) return;
    try {
      await API.delete(`/clients/${id}`, { params: { permanent: true } });
      enqueueSnackbar('Cliente eliminado permanentemente', { variant: 'success' });
      fetchClients();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al eliminar';
      const wantsDeactivate = window.confirm(`${msg}\n\n¿Deseas desactivarlo en su lugar? (deja de aparecer en listados pero conserva su historial)`);
      if (wantsDeactivate) {
        try {
          await API.delete(`/clients/${id}`);
          enqueueSnackbar('Cliente desactivado', { variant: 'info' });
          fetchClients();
        } catch {
          enqueueSnackbar('Error al desactivar', { variant: 'error' });
        }
      }
    }
  };

  const avatarColor = (name) => {
    const colors = ['#6B21A8', '#0E7490', '#065F46', '#92400E', '#1E3A5F'];
    return colors[(name?.charCodeAt(0) || 0) % colors.length];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E1B2E' }}>Clientes</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{total} clientes registrados</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>
          Nuevo cliente
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por nombre, RFC, email…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          />
          <Select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} displayEmpty size="small" sx={{ minWidth: 140 }}>
            <MenuItem value="">Todos los estados</MenuItem>
            {STATUS_OPTIONS.filter(Boolean).map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
          </Select>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>RFC</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>Alta</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} />
              </TableCell></TableRow>
            ) : clients.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                No se encontraron clientes
              </TableCell></TableRow>
            ) : clients.map(c => (
              <TableRow key={c._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, background: avatarColor(c.name), fontSize: '0.85rem', fontWeight: 700 }}>
                      {c.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                      {c.businessName && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.businessName}</Typography>}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    {c.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                      <Typography variant="caption">{c.email}</Typography>
                    </Box>}
                    {c.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                      <Typography variant="caption">{c.phone}</Typography>
                    </Box>}
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.rfc || '-'}</Typography></TableCell>
                <TableCell><Chip label={STATUS_LABELS[c.status]} color={STATUS_COLORS[c.status]} size="small" /></TableCell>
                <TableCell><Typography variant="body2">{c.seller?.name || '-'}</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmt.date(c.createdAt)}</Typography></TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={() => navigate(`/clientes/${c._id}`)}><Visibility fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => { setEditClient(c); setFormOpen(true); }}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => deleteClient(c._id, c.name)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={15}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[15]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Card>

      <ClientFormDialog open={formOpen} client={editClient}
        onClose={() => { setFormOpen(false); setEditClient(null); }} onSave={handleSave} />
    </Box>
  );
}
